import type { PricingPlan, PricingProps } from "@emkit/builder-widgets";
import { useEditor } from "../../store.js";
import { CheckboxField, ColorField, SelectField, TextField } from "./fields.js";
import type { WidgetInspectorProps } from "./registry.js";

const COLS = ["2", "3", "4"] as const;

function newPlanId(): string {
  return `p-${Math.random().toString(36).slice(2, 8)}`;
}

export function PricingInspector({ widget }: WidgetInspectorProps) {
  const update = useEditor((s) => s.updateWidgetProps);
  const p = widget.props as PricingProps;
  const plans = p.plans ?? [];

  const setPlans = (next: PricingPlan[]) => update(widget.id, { plans: next });

  const addPlan = () =>
    setPlans([
      ...plans,
      {
        id: newPlanId(),
        name: "New plan",
        price: "$0",
        period: "/mo",
        features: ["Feature"],
        ctaLabel: "Choose",
        ctaHref: "#",
      },
    ]);

  const updatePlan = (id: string, patch: Partial<PricingPlan>) =>
    setPlans(plans.map((pl) => (pl.id === id ? { ...pl, ...patch } : pl)));

  const removePlan = (id: string) => setPlans(plans.filter((pl) => pl.id !== id));

  const movePlan = (id: string, dir: "up" | "down") => {
    const idx = plans.findIndex((pl) => pl.id === id);
    if (idx < 0) return;
    const target = dir === "up" ? idx - 1 : idx + 1;
    if (target < 0 || target >= plans.length) return;
    const next = plans.slice();
    const moved = next.splice(idx, 1)[0];
    if (!moved) return;
    next.splice(target, 0, moved);
    setPlans(next);
  };

  return (
    <>
      <SelectField
        label="Columns"
        value={String(p.columns ?? 3) as (typeof COLS)[number]}
        options={COLS}
        onChange={(v) =>
          update(widget.id, {
            columns: Number.parseInt(v, 10) as PricingProps["columns"],
          })
        }
      />
      <ColorField
        label="Accent"
        value={p.accent ?? "#111827"}
        onChange={(v) => update(widget.id, { accent: v })}
      />
      <ColorField
        label="Card background"
        value={p.cardBackground ?? "#ffffff"}
        onChange={(v) => update(widget.id, { cardBackground: v })}
      />
      <ColorField
        label="Text"
        value={p.textColor ?? "#0f172a"}
        onChange={(v) => update(widget.id, { textColor: v })}
      />

      <div className="emke-section-title" style={{ marginTop: 8 }}>
        Plans ({plans.length})
      </div>

      {plans.map((pl, i) => (
        <div
          key={pl.id}
          style={{
            padding: "8px 16px 10px",
            borderBottom: "1px solid var(--border)",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: 4,
              justifyContent: "flex-end",
              marginBottom: 4,
            }}
          >
            <button
              type="button"
              className="emke-btn"
              onClick={() => movePlan(pl.id, "up")}
              disabled={i === 0}
              title="Move up"
              style={{ padding: "2px 6px", opacity: i === 0 ? 0.4 : 1 }}
            >
              ↑
            </button>
            <button
              type="button"
              className="emke-btn"
              onClick={() => movePlan(pl.id, "down")}
              disabled={i === plans.length - 1}
              title="Move down"
              style={{
                padding: "2px 6px",
                opacity: i === plans.length - 1 ? 0.4 : 1,
              }}
            >
              ↓
            </button>
            <button
              type="button"
              className="emke-btn is-danger"
              onClick={() => removePlan(pl.id)}
              title="Delete plan"
              style={{ padding: "2px 6px" }}
            >
              ✕
            </button>
          </div>

          <TextField
            label={`Plan ${i + 1} name`}
            value={pl.name}
            onChange={(v) => updatePlan(pl.id, { name: v })}
          />
          <TextField
            label="Description"
            value={pl.description ?? ""}
            onChange={(v) => updatePlan(pl.id, { description: v || undefined })}
          />
          <div className="emke-inspector-grid-2" style={{ padding: "0" }}>
            <TextField
              label="Price"
              value={pl.price}
              onChange={(v) => updatePlan(pl.id, { price: v })}
            />
            <TextField
              label="Period"
              value={pl.period ?? ""}
              onChange={(v) => updatePlan(pl.id, { period: v || undefined })}
            />
          </div>
          <TextField
            label="Features (one per line)"
            value={(pl.features ?? []).join("\n")}
            multiline
            onChange={(v) =>
              updatePlan(pl.id, {
                features: v
                  .split("\n")
                  .map((x) => x.trim())
                  .filter(Boolean),
              })
            }
          />
          <div className="emke-inspector-grid-2" style={{ padding: "0" }}>
            <TextField
              label="CTA label"
              value={pl.ctaLabel}
              onChange={(v) => updatePlan(pl.id, { ctaLabel: v })}
            />
            <TextField
              label="CTA URL"
              value={pl.ctaHref}
              onChange={(v) => updatePlan(pl.id, { ctaHref: v })}
            />
          </div>
          <CheckboxField
            label="Highlight this plan"
            value={pl.highlight ?? false}
            onChange={(v) => updatePlan(pl.id, { highlight: v })}
          />
          {pl.highlight && (
            <TextField
              label="Highlight badge"
              value={pl.highlightBadge ?? ""}
              onChange={(v) => updatePlan(pl.id, { highlightBadge: v || undefined })}
            />
          )}
        </div>
      ))}

      <div style={{ padding: "12px 16px 0" }}>
        <button
          type="button"
          className="emke-btn"
          onClick={addPlan}
          style={{ width: "100%" }}
        >
          + Add plan
        </button>
      </div>
    </>
  );
}
